import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import RadioButtonUncheckedRoundedIcon from '@material-ui/icons/RadioButtonUncheckedRounded';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import ChevronRightRoundedIcon from '@material-ui/icons/ChevronRightRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { apiList, verifyLogin } from '../../helpers';

// Side panel for choosing a report format (progress report, DRF, ...).
// Loads the formats of `type` when opened, preselects the first one and
// calls onGenerate(formatId) — the same contract as the old dialog.
//
// `blockers` ({ title, message, items: [{ key, title, subtitle, status, href }] })
// replaces the format list when the report can't be generated yet, e.g.
// activities still waiting to be verified or rejected.

const useStyles = makeStyles({
  paper: {
    width: 420,
    maxWidth: '100vw',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '16px 0 0 16px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '20px 20px 16px 24px',
    borderBottom: '1px solid #EEF1F6',
  },
  headerText: { flex: 1 },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: '#1F2733',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#5B6472',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 24px',
  },
  label: {
    margin: '0 0 10px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#8A94A6',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #D7DEEA',
    borderRadius: 12,
    background: '#fff',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
    '&:hover': { borderColor: '#B9C3D6', background: '#FAFBFD' },
    '&:focus-visible': { outline: 'none', boxShadow: '0 0 0 3px rgba(69, 130, 255, 0.3)' },
  },
  optionSelected: {
    borderColor: '#2E3B6E',
    background: '#F4F6FB',
    boxShadow: '0 0 0 1px #2E3B6E',
    '&:hover': { borderColor: '#2E3B6E', background: '#F4F6FB' },
  },
  optionIcon: {
    flex: '0 0 auto',
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(69, 130, 255, 0.10)',
    color: '#2F6BEA',
  },
  optionName: {
    flex: 1,
    fontSize: 15,
    fontWeight: 500,
    color: '#1F2733',
    overflowWrap: 'anywhere',
  },
  radio: { color: '#B9C3D6' },
  radioOn: { color: '#2E3B6E' },
  state: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
    fontSize: 14,
    color: '#8A94A6',
    textAlign: 'center',
  },
  notice: {
    display: 'flex',
    gap: 12,
    padding: '14px 16px',
    marginBottom: 18,
    borderRadius: 12,
    background: '#FFFAEB',
    border: '1px solid #FEDF89',
    color: '#93370D',
    fontSize: 14,
    lineHeight: 1.5,
  },
  noticeIcon: { color: '#DC6803', flex: '0 0 auto', marginTop: 1 },
  noticeTitle: { margin: '0 0 2px', fontWeight: 600, color: '#7A2E0E' },
  noticeText: { margin: 0 },
  pendingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    border: '1px solid #EAEEF5',
    borderRadius: 12,
    textDecoration: 'none',
    color: 'inherit',
    transition: 'border-color 0.15s ease, background 0.15s ease',
    '&:hover': { borderColor: '#B9C3D6', background: '#FAFBFD' },
    '&:focus-visible': { outline: 'none', boxShadow: '0 0 0 3px rgba(69, 130, 255, 0.3)' },
  },
  pendingText: { flex: 1, minWidth: 0 },
  pendingTitle: {
    display: 'block', fontSize: 15, fontWeight: 500, color: '#1F2733',
  },
  pendingSub: {
    display: 'block', marginTop: 2, fontSize: 13, color: '#5B6472',
  },
  chip: {
    flex: '0 0 auto',
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    background: '#FFFAEB',
    color: '#B54708',
  },
  chipUploading: { background: '#EFF4FF', color: '#1849A9' },
  chevron: { color: '#8A94A6', flex: '0 0 auto' },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '16px 24px',
    borderTop: '1px solid #EEF1F6',
    background: '#FAFBFD',
  },
});

export default function ReportFormatPanel({
  open, onClose, type, title, subtitle, actionLabel = 'Generate report', onGenerate, blockers,
}) {
  const classes = useStyles();
  const [formats, setFormats] = useState([]);
  const [selected, setSelected] = useState(-1);
  const [loading, setLoading] = useState(false);
  const blocked = Boolean(blockers && blockers.items && blockers.items.length);

  useEffect(() => {
    if (!open || blocked) return undefined;
    let active = true;
    setLoading(true);
    const { session } = verifyLogin();
    axios.get(`${apiList.GET_REPORT_FORMAT}?type=${type}`, { headers: { session } })
      .then((response) => {
        if (!active) return;
        const list = response?.data?.data || [];
        setFormats(list);
        setSelected(list.length ? list[0].id : -1);
      })
      .catch(() => {
        if (active) setFormats([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [open, type, blocked]);

  let content;
  if (blocked) {
    content = (
      <>
        <div className={classes.notice} role='alert'>
          <WarningRoundedIcon className={classes.noticeIcon} />
          <div>
            <p className={classes.noticeTitle}>{blockers.title}</p>
            <p className={classes.noticeText}>{blockers.message}</p>
          </div>
        </div>
        <p className={classes.label}>{`Needs action (${blockers.items.length})`}</p>
        <div className={classes.list}>
          {blockers.items.map((item) => (
            <a key={item.key} className={classes.pendingItem} href={item.href}>
              <span className={classes.pendingText}>
                <span className={classes.pendingTitle}>{item.title}</span>
                {item.subtitle ? <span className={classes.pendingSub}>{item.subtitle}</span> : null}
              </span>
              <span className={`${classes.chip} ${item.status === 'Uploading' ? classes.chipUploading : ''}`}>
                {item.status}
              </span>
              <ChevronRightRoundedIcon className={classes.chevron} />
            </a>
          ))}
        </div>
      </>
    );
  } else if (loading) {
    content = <div className={classes.state}><CircularProgress size={28} /></div>;
  } else if (!formats.length) {
    content = (
      <div className={classes.state}>No report formats are set up for your organisation.</div>
    );
  } else {
    content = (
      <>
        <p className={classes.label}>Report format</p>
        <div className={classes.list} role='radiogroup' aria-label='Report format'>
          {formats.map((format) => {
            const isOn = format.id === selected;
            return (
              <button
                key={format.id}
                type='button'
                role='radio'
                aria-checked={isOn}
                className={`${classes.option} ${isOn ? classes.optionSelected : ''}`}
                onClick={() => setSelected(format.id)}
              >
                <span className={classes.optionIcon}><DescriptionOutlinedIcon fontSize='small' /></span>
                <span className={classes.optionName}>{format.name}</span>
                {isOn
                  ? <CheckCircleRoundedIcon className={classes.radioOn} />
                  : <RadioButtonUncheckedRoundedIcon className={classes.radio} />}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <h2 className={classes.title}>{title}</h2>
          {subtitle && !blocked ? <p className={classes.subtitle}>{subtitle}</p> : null}
        </div>
        <IconButton aria-label='Close' size='small' onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </div>
      <div className={classes.body}>{content}</div>
      <div className={classes.footer}>
        {blocked ? (
          <Button variant='contained' color='primary' onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button color='primary' onClick={onClose}>Cancel</Button>
            <Button
              variant='contained'
              color='primary'
              disabled={selected === -1 || loading}
              onClick={() => onGenerate(selected)}
            >
              {actionLabel}
            </Button>
          </>
        )}
      </div>
    </Drawer>
  );
}
